import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import SidepanelIcon from '../../../packages/svgs/SidepanelIcon';
import { Search, X } from 'lucide-react';

interface ChitraHeaderProps {
  isOpen: boolean;
  onToggle: () => void;
  isDarkMode: boolean;
  isHeaderVisible: boolean;
}

export const ChitraHeader: React.FC<ChitraHeaderProps> = ({ isOpen, onToggle, isDarkMode, isHeaderVisible }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const isLibraryPage = location.pathname.includes('/chitra/library');
  const searchQuery = React.useMemo(() => {
    try {
      const q = new URLSearchParams(location.search).get('q') || '';
      return q.trim();
    } catch {
      return '';
    }
  }, [location.search]);

  return (
    <header
      className={cn(
        'fixed top-0 left-0 right-0 z-[100] bg-transparen transition-transform duration-300',
        isHeaderVisible ? 'translate-y-0' : '-translate-y-full'
      )}
    >
      <div className="relative w-full px-4 pt-4">
        <div className="relative w-full h-10">
          <div className="absolute left-0 top-0 flex items-center gap-4">
            <button
              onClick={() => onToggle()}
              aria-label="Toggle sidebar"
              className={cn(
                'p-1 transition-colors duration-150 rounded-xl flex items-center justify-center',
                isDarkMode
                  ? 'text-slate-300 hover:text-white hover:bg-white/10'
                  : 'text-black/90 hover:text-slate-900 hover:bg-slate-200/70'
              )}
            >
              <SidepanelIcon className="h-6 w-6" isDarkMode={isDarkMode} />
            </button>
            <h1 className="text-2xl instrument-serif-regular">Chitra</h1>
          </div>

          <div className="absolute right-0 top-0 flex items-center gap-2">
            <div
              className={cn(
                'transition-all duration-300 ease-in-out',
                isLibraryPage ? 'opacity-0 scale-95 pointer-events-none w-0 overflow-hidden' : 'opacity-100 scale-100'
              )}
            >
              <button
                onClick={() => {
                  window.dispatchEvent(new CustomEvent('chitra:openSearch'));
                }}
                aria-label="Search"
                className={cn(
                  'rounded-full border backdrop-blur-sm px-3 py-2 transition-colors duration-150 flex items-center gap-2',
                  isDarkMode
                    ? 'bg-white/5 hover:bg-white/10 text-white border-white/10'
                    : 'bg-white/60 hover:bg-white/80 text-black border-black/10'
                )}
              >
                <Search className="h-5 w-5" />
                {searchQuery ? (
                  <>
                    <span className={cn('text-sm font-medium max-w-[10rem] truncate')}>{searchQuery}</span>
                    <span
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate('/chitra/home', { replace: true });
                      }}
                      aria-label="Clear search"
                      className={cn(
                        'flex h-6 w-6 items-center justify-center rounded-full text-xs transition-colors',
                        isDarkMode ? 'hover:bg-white/10' : 'hover:bg-black/10'
                      )}
                    >
                      <X className="h-3 w-3" />
                    </span>
                  </>
                ) : (
                  <>
                    <span className={cn('text-sm font-medium hidden sm:inline')}>Search</span>
                    <span className={cn('text-sm opacity-60 hidden lg:inline')}>Ctrl K</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default ChitraHeader;
